import type React from 'react';

import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined';
import BusinessIcon from '@mui/icons-material/Business';
import DescriptionIcon from '@mui/icons-material/Description';
import EqualizerIcon from '@mui/icons-material/Equalizer';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import GroupsIcon from '@mui/icons-material/Groups';
import PersonIcon from '@mui/icons-material/Person';
import PsychologyIcon from '@mui/icons-material/Psychology';
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye';
import SearchIcon from '@mui/icons-material/Search';
import ThreePIcon from '@mui/icons-material/ThreeP';
import TrackChangesIcon from '@mui/icons-material/TrackChanges';

import { NotificationTypeDto } from '@/api/generated/models/notification-type-dto';
import { RoleTypeDto } from '@/api/generated/models/role-type-dto';
import CertificatesIcon from '@/assets/icons/userProfile/CertificatesIcon.png';
import OfferingsIcon from '@/assets/icons/userProfile/OfferingsIcon.svg';
import { MaskedSvgIcon } from '@/components/ui/icons/MaskedSvgIcon.comp.tsx';
import { RouteEnum } from '@/layouts/routes/Route.model.ts';
import { type DictionaryManagementType } from '@/views/dictionaryManagement/model/DictionaryPairManagementConfig.model.ts';

const props = {
  sx: {
    color: 'white',
  },
};

export interface MenuGroupModel {
  id: MenuGroupType;
  items: MenuItemModel[];
}

export type MenuGroupType = 'administration' | 'projectsRequests' | 'tools' | 'userProfiles';

export interface MenuItemModel {
  badgeFor?: RoleTypeDto[];
  badgeType?: NotificationTypeDto;
  icon: React.JSX.Element;
  id: MenuItemType;
  route: RouteEnum;
}

export type MenuItemType =
  | 'addVacancy'
  | 'assessments'
  | 'companies'
  | 'overview'
  | 'profileSearch'
  | 'projects'
  | 'recruitmentBoard'
  | 'recruitmentCandidates'
  | 'reports'
  | 'settingsDictionaries'
  | 'userProfile'
  | DictionaryManagementMenuItem;

type DictionaryManagementMenuItem = `DICTIONARY_MANAGEMENT_${DictionaryManagementType}`;

export const MENU_GROUPS: MenuGroupModel[] = [
  {
    id: 'userProfiles',
    items: [
      {
        icon: <PersonIcon {...props} />,
        id: 'userProfile',
        route: RouteEnum.USER_PROFILE,
      },
      {
        icon: <SearchIcon {...props} />,
        id: 'profileSearch',
        route: RouteEnum.PROFILE_SEARCH,
      },
    ],
  },
  {
    id: 'projectsRequests',
    items: [
      {
        icon: <FormatListBulletedIcon {...props} />,
        id: 'projects',
        route: RouteEnum.VACANCY_PROJECTS,
      },
      {
        icon: <ThreePIcon {...props} />,
        id: 'addVacancy',
        route: RouteEnum.ADD_VACANCY,
      },
      {
        icon: <TrackChangesIcon {...props} />,
        id: 'recruitmentBoard',
        route: RouteEnum.RECRUITMENT_BOARD,
      },
      {
        icon: <GroupsIcon {...props} />,
        id: 'recruitmentCandidates',
        route: RouteEnum.CANDIDATES,
      },
    ],
  },
  {
    id: 'tools',
    items: [
      {
        badgeFor: [RoleTypeDto.Admin],
        badgeType: NotificationTypeDto.AccountsAwaitingApproval,
        icon: <BusinessIcon {...props} />,
        id: 'companies',
        route: RouteEnum.COMPANIES,
      },
      {
        icon: <EqualizerIcon {...props} />,
        id: 'reports',
        route: RouteEnum.REPORTS,
      },
      {
        icon: <RemoveRedEyeIcon {...props} />,
        id: 'overview',
        route: RouteEnum.OVERVIEW,
      },
      {
        icon: <AssessmentOutlinedIcon {...props} />,
        id: 'assessments',
        route: RouteEnum.ASSESSMENTS,
      },
    ],
  },
  {
    id: 'administration',
    items: [
      {
        icon: <DescriptionIcon {...props} />,
        id: 'settingsDictionaries',
        route: RouteEnum.DICTIONARIES,
      },
      {
        icon: <PsychologyIcon {...props} />,
        id: 'DICTIONARY_MANAGEMENT_SKILL',
        route: RouteEnum.SKILLS_MANAGEMENT,
      },
      {
        icon: <MaskedSvgIcon src={OfferingsIcon as string} {...props} />,
        id: 'DICTIONARY_MANAGEMENT_OFFERING',
        route: RouteEnum.OFFERINGS_MANAGEMENT,
      },
      {
        icon: <MaskedSvgIcon src={CertificatesIcon} {...props} />,
        id: 'DICTIONARY_MANAGEMENT_CERTIFICATE',
        route: RouteEnum.CERTIFICATES_MANAGEMENT,
      },
    ],
  },
];

// Legacy export for backward compatibility
export const MENU_ITEMS: MenuItemModel[] = MENU_GROUPS.flatMap(group => group.items);
